import { CloseOutlined } from '@ant-design/icons'
import { Drawer, Switch } from 'antd'
import React, { useEffect, useRef, useState } from 'react'
import '../userProfileAttendanceDetailsV3.css'
import AnimatedDatePicker from 'components/AnimatedDatePicker'
import SaveUserBasicProfileV3 from 'pages/profile/saveUserBasicProfileV3';
import { Button } from 'components/Button'
import { connect } from 'react-redux'
import moment from 'moment'
import { getWebClockIn, postWebClockIn, setClockInEndDate, setClockInStartDate, setClockInSwitch } from 'actions/attendanceNew'

const SettingsDrawer = ({
    settingsDrawerVisible,
    setSettingsDrawerVisible,
    isChecked,
    setIsChecked,
    // "Allow mark location" — commented out for now (web clock in/out only).
    // isChecked1,
    // setIsChecked1,
    durationSettings,
    setDurationSettings,
    userId,
    getWebClockIn,
    postWebClockIn,
    setClockInStartDate,
    setClockInEndDate,
    setClockInSwitch, storeClockInData,
}) => {
    const [buttonLoader, setButtonLoader] = useState(false);
    const { duration, startDate, endDate } = durationSettings;
    const [initialValues, setInitialValues] = useState({});
    const [hasChanges, setHasChanges] = useState(false);
    const [showSaveModal, setShowSaveModal] = useState(false);
    // Set once the user toggles the switch or picks a date; reset on close.
    // getWebClockIn's response lands a moment after opening and used to
    // overwrite (switch back off) what the user had just changed.
    const userEditedRef = useRef(false);

    useEffect(() => {
        if (settingsDrawerVisible && userId) {
            getWebClockIn(userId);
        }
    }, [settingsDrawerVisible, userId]);

    useEffect(() => {
        if (!settingsDrawerVisible) {
            userEditedRef.current = false;
            return;
        }

        const data = getParsedData(storeClockInData);

        if (!data) return;

        const enabled = data.enabled === true || data.enabled === 1 || data.enabled === "1" || data.enabled === "true";

        const settings = getParsedSettings(data);

        const savedDuration = settings?.duration || "Permanent";
        const savedStartDate = settings?.startDate ? moment(settings.startDate).format("DD/MM/YYYY") : "";
        const savedEndDate = settings?.endDate ? moment(settings.endDate).format("DD/MM/YYYY") : "";

        if (!userEditedRef.current) {
            setIsChecked(enabled);
            setDurationSettings({
                duration: savedDuration,
                startDate: savedStartDate,
                endDate: savedEndDate,
            });
        }

        setInitialValues({
            isChecked: enabled,
            // isChecked1,
            duration: savedDuration,
            startDate: savedStartDate,
            endDate: savedEndDate,
        });

    }, [storeClockInData, settingsDrawerVisible]);

    useEffect(() => {
        const changed = initialValues.isChecked !== isChecked ||
            // initialValues.isChecked1 !== isChecked1 ||
            initialValues.duration !== duration ||
            moment(initialValues.startDate, "DD/MM/YYYY").format("YYYY-MM-DD") !==
            moment(startDate, "DD/MM/YYYY").format("YYYY-MM-DD") ||
            moment(initialValues.endDate, "DD/MM/YYYY").format("YYYY-MM-DD") !==
            moment(endDate, "DD/MM/YYYY").format("YYYY-MM-DD");

        setHasChanges(changed);
    }, [isChecked, /* isChecked1, */ duration, startDate, endDate, initialValues,]);

    const getParsedData = (raw) => {
        let data = Array.isArray(raw) ? raw[0] : raw;

        if (data && data.data) {
            data = Array.isArray(data.data) ? data.data[0] : data.data;
        }

        return data;
    };

    const getParsedSettings = (dataObj) => {
        let settings = dataObj?.settings;

        if (typeof settings === "string") {
            try {
                settings = JSON.parse(settings);
            } catch (e) { }
        }

        return settings;
    };

    const handleToggle = (checked) => {
        userEditedRef.current = true;
        setIsChecked(checked);
        setClockInSwitch(checked);

        // Web clock in/out can only be applied as Temporary for now.
        if (checked && !initialValues.isChecked) {
            setDurationSettings({ duration: "Temporary", startDate: "", endDate: "", });
            setClockInStartDate(null);
            setClockInEndDate(null);
        } else if (checked) {
            setDurationSettings((prev) => ({ ...prev, duration: "Temporary" }));
        }

        if (!checked) {
            setDurationSettings({ duration: "Permanent", startDate: "", endDate: "", });
            setClockInStartDate(null);
            setClockInEndDate(null);
        }
    };

    // const handleToggle1 = (checked) => {
    //     setIsChecked1(checked);
    // };

    // Duration pills hidden for now (only Temporary is allowed).
    // const types = [
    //     { value: "Permanent", label: "Permanent" },
    //     { value: "Temporary", label: "Temporary" },
    // ];

    const onClose = (e) => {
        setSettingsDrawerVisible(false);
    };

    // const handleTypeToggle = (val) => {
    //     if (duration === val) return;
    //
    //     setDurationSettings({ ...durationSettings, duration: val, startDate: "", endDate: "", });
    //
    //     if (val === "Temporary" && initialValues.duration !== "Temporary") {
    //         setClockInStartDate(null);
    //         setClockInEndDate(null);
    //     }
    // };

    const handleDateChange = (key, value) => {
        userEditedRef.current = true;
        const formatted = value ? value.format("DD/MM/YYYY") : "";

        // Start date cleared -> clear the end date too (it's disabled until a start is picked).
        const clearEnd = key === "startDate" && !value;
        setDurationSettings({ ...durationSettings, duration: "Temporary", [key]: formatted, ...(clearEnd ? { endDate: "" } : {}) });

        if (key === "startDate") {
            setClockInStartDate(value);
            if (clearEnd) setClockInEndDate(null);
        } else {
            setClockInEndDate(value);
        }
    };

    const disabledStartDate = (current) => {
        return current && current < moment().startOf("day");
    };

    const disabledEndDate = (current) => {
        if (!startDate) {
            return current && current < moment().startOf("day");
        }

        const selectedStart = moment(startDate, "DD/MM/YYYY", true);

        if (!selectedStart.isValid()) {
            return current && current < moment().startOf("day");
        }

        return current && current.isBefore(selectedStart, "day");
    };

    const isSaveDisabled = !hasChanges || (isChecked && duration === "Temporary" && (!startDate || !endDate || moment(startDate).isAfter(moment(endDate))));

    const handleSaveClick = () => {
        setButtonLoader(true);
        setClockInSwitch(isChecked);
        setClockInStartDate(startDate ? moment(startDate, "DD/MM/YYYY") : null);
        setClockInEndDate(endDate ? moment(endDate, "DD/MM/YYYY") : null);

        postWebClockIn(isChecked, userId, duration || 'Permanent', () => {
            setButtonLoader(false);
            setSettingsDrawerVisible(false);
        });
    };
    const handleConfirmSave = (closeModal, stopLoader) => {
        setButtonLoader(true);
        setClockInSwitch(isChecked);
        setClockInStartDate(
            startDate ? moment(startDate, "DD/MM/YYYY") : null
        );
        setClockInEndDate(
            endDate ? moment(endDate, "DD/MM/YYYY") : null
        );

        postWebClockIn(
            isChecked,
            userId,
            duration || "Permanent",
            () => {
                setButtonLoader(false);
                stopLoader && stopLoader();
                closeModal && closeModal();

                setShowSaveModal(false);
                setSettingsDrawerVisible(false);
            }
        );
    };

    return (
        <Drawer
            // title="Select range"
            placement="right"
            onClose={onClose}
            visible={settingsDrawerVisible}
            width={450}
            closable={false}
        >
            <div className='drawer-title'>
                <div style={{ fontWeight: 500, fontSize: 18 }}> Settings</div>
                <div><CloseOutlined style={{ color: '#AEAEAE', fontSize: 18 }} onClick={onClose} /></div>
            </div>
            <div className='p-20 drawer-height' >
                <div className="switch-container">
                    <div>Web clock in/clock out</div>
                    <div><Switch checked={isChecked} onChange={handleToggle} /></div>
                </div>

                {/* Duration (Permanent / Temporary) — hidden for now, only Temporary is allowed.
                {isChecked ?
                    <div>
                        <div className='sub-headline-v3'>Duration</div>
                        <div style={{ display: "flex", flexWrap: "wrap" }}>
                            {types?.map((item) => (
                                <div
                                    key={item.value}
                                    className='sortByFilter'
                                    style={{
                                        backgroundColor: duration == item.value ? "#D6C8F5" : '#FFFFFF',
                                        color: duration == item.value ? '#191919' : "#636363",
                                        border: duration == item.value ? 'none' : '1px solid #E6E6E6',
                                        maxWidth: 130,
                                        minWidth: 100,
                                        alignItems: 'center',
                                    }}

                                    onClick={() => handleTypeToggle(item.value)}
                                >

                                    {item.label}
                                </div>
                            ))}
                        </div>
                    </div>
                    :
                    <></>
                }
                */}

                {isChecked
                    ?
                    <div className='centered-flex-v3'>
                        <div className='temp-date-picker'>
                            <AnimatedDatePicker
                                inputId="startDate"
                                inputReadOnly={true}
                                label={"Start date"}
                                value={startDate}
                                allowClear
                                handleDateChange={(value) => handleDateChange('startDate', value)}
                                disabledDate={disabledStartDate}
                            />
                        </div>
                        <div className='temp-date-picker' style={{ opacity: startDate ? 1 : 0.5 }}>
                            <AnimatedDatePicker
                                inputId="endDate"
                                inputReadOnly={true}
                                label={"End date"}
                                value={endDate}
                                allowClear
                                handleDateChange={(value) => handleDateChange('endDate', value)}
                                disabled={!startDate}
                                disabledDate={disabledEndDate}
                            />
                        </div>
                    </div>
                    :
                    <></>
                }
                {/* "Allow mark location" — commented out for now (web clock in/out only).
                <div className="switch-container m-t-20">
                    <div>Allow mark location</div>
                    <div><Switch checked={isChecked1} onChange={handleToggle1} /></div>
                </div>
                */}
            </div>
            <div
                style={{
                    display: "flex",
                    justifyContent: "space-between",
                    padding: '0px 20px 0px 20px'
                }}
            >

                <Button type="" style={{
                    backgroundColor: "#FFFFFF", color: "#AEAEAE", borderRadius: 8, border: '2px solid #AEAEAE', display: 'flex',
                    justifyContent: 'center',
                    alignItems: 'center',
                    fontWeight: 500
                }}
                    onClick={onClose} >CANCEL</Button>
                <Button
                    loading={buttonLoader}
                    disabled={isSaveDisabled}
                    className="m-l-30"
                    style={{
                        borderRadius: 8,
                        display: 'flex',
                        justifyContent: 'center',
                        alignItems: 'center',
                        backgroundColor: "#594099",
                        color: '#FFFFFF',
                        opacity: isSaveDisabled ? 0.5 : 1,
                    }}
                    type="primary"
                    onClick={() => setShowSaveModal(true)}
                >
                    SAVE
                </Button>
            </div>
            <SaveUserBasicProfileV3
                saveConfirmationModal={showSaveModal}
                onCancel={() => setShowSaveModal(false)}
                handleOk={handleConfirmSave}
                flag={false}
            />
        </Drawer>
    )
}
const mapStateToProps = (state) => {
    const { storeClockInData, storeClockInStartDate, storeClockInEndDate, storeClockInSwitch, storeClockInLoading } = state.attendanceNew;
    return { storeClockInData, storeClockInStartDate, storeClockInEndDate, storeClockInSwitch, storeClockInLoading };
};

const mapDispatchToProps = (dispatch) => ({
    getWebClockIn: (storeUserId) => dispatch(getWebClockIn(storeUserId)),
    postWebClockIn: (isSwitch, storeUserId, type, callBack) => dispatch(postWebClockIn(isSwitch, storeUserId, type, callBack)),
    setClockInStartDate: (res) => dispatch(setClockInStartDate(res)),
    setClockInEndDate: (res) => dispatch(setClockInEndDate(res)),
    setClockInSwitch: (res) => dispatch(setClockInSwitch(res)),
});

export default connect(mapStateToProps, mapDispatchToProps)(SettingsDrawer)